
import { MarvDateService } from '@marv/services';
import { MissionStatus, OperationCustomerPlanningSetting } from '@app/models';
import { 
    MissionItem, MissionItemRetailer, MissionItemStore, MissionItemArea, MissionItemSector, 
    MissionItemOperation, MissionItemSalesRep 
} from '@app/components';
import { OperationService, CustomerFeedbackService } from '@app/services';

export class MissionPlanningLogaObjective {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLogaTypeMission {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLogaOperation {
    id: number;
    code: string;
    name: string;
    typeOfMission: MissionPlanningLogaTypeMission;
}

export class MissionPlanningLogaWorker {
    workerId: number;
    workerName: string;
    workerEmail: string;
    workerMobilePhone: string;
}

export class MissionPlanningLogaProfile {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLogaMission {
    id: number;
    code: string;
    customerFeedback: string;
    profile: MissionPlanningLogaProfile;
    status: MissionStatus;
}

export class MissionPlanningLogaLocationAddress {
    line1: string;
    line2: string;
    administrativeArea: string;
    zipCode: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
}

export class MissionPlanningLogaLocation {
    id: number;
    code: string;
    name: string;
    address: MissionPlanningLogaLocationAddress;
}

export class MissionPlanningLogaRetailer {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLogaSector {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLogaArea {
    id: number;
    code: string;
    name: string;
}

export class MissionPlanningLoga {
    id: number;
    day: number;
    amStartTime: Date;
    amEndTime: Date;
    pmStartTime: Date;
    pmEndTime: Date;
    requestedDurationMin: number|null;
    code: string;
    declaredPresence: boolean;
    confirmedPresence: boolean;
    recoCompliance: boolean;
    reportPublished: boolean;
    reportable: boolean;
    reportingStatus: string;
    reportingCompleted: boolean;
    mission: MissionPlanningLogaMission;
    operation: MissionPlanningLogaOperation;
    worker: MissionPlanningLogaWorker;
    location: MissionPlanningLogaLocation;
    retailer: MissionPlanningLogaRetailer;
    customerSector: MissionPlanningLogaSector;
    customerArea: MissionPlanningLogaArea;
    plannedPurposes: Array<MissionPlanningLogaObjective> = [];
    
    static FromJson(o: any): MissionPlanningLoga {
        let logaEntry = o;
        logaEntry.amStartTime = (o.amStartTime ? new Date(o.amStartTime) : null);
        logaEntry.amEndTime = (o.amEndTime ? new Date(o.amEndTime) : null);
        logaEntry.pmStartTime = (o.pmStartTime ? new Date(o.pmStartTime) : null);
        logaEntry.pmEndTime = (o.pmEndTime ? new Date(o.pmEndTime) : null);
        logaEntry.declaredPresence = (o.declaredPresence === 'YES');
        logaEntry.confirmedPresence = (o.confirmedPresence === 'YES');
        logaEntry.reportingCompleted = (o.reportingStatus === 'COMPLETE');        
        if(o.mission?.status){
            logaEntry.mission.status = MissionStatus.FromOrder(o.mission.status, o.day);
        }
        else{
            logaEntry.mission.status = new MissionStatus(MissionStatus.Status.PENDING_VALIDATION, o.day)
        }        
        return logaEntry;
    }
}

export class MissionPlanningPearlsObjective {
    code: string;
    fulfilled: boolean;
}

export class MissionPlanningPearlsAddress {
    line1: string;
    line2: string;
    administrativeArea: string;
    zipCode: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
}

export class MissionPlanningPearls {
    id: number;
    logaDay: number;
    startTime: string;
    endTime: string;
    hrDuration: string;
    ctxId: number;
    salesRepId: number;
    salesRepName: string;
    storeCode: string;
    storeId: number;
    storeName: string;
    retailerId: number;
    retailerCode: string;
    retailerName: string;
    retailerIconUrl: string;
    address: MissionPlanningPearlsAddress;
    sectorId: number;
    sectorName: string;
    areaId: number;
    areaName: string;
    checkInDone: boolean;
    status: MissionStatus;
    statusClass: string;
    description: string;    
    nbPictures: number;
    objectives: Array<MissionPlanningPearlsObjective> = [];
    planningEntryType: string;   
    
    static FromJson(o: any): MissionPlanningPearls {
        let pearlsEntry = o;  
        pearlsEntry.status = MissionStatus.FromPearlsPlanningEntry(o.status, o.logaDay);      
        return pearlsEntry;
    }
}

export class MissionPlanningDay {
    dayLabel: string;
    logaDay: number;
    logaPlanningEntries: Array<MissionPlanningLoga> = [];
    pearlsPlanningEntries: Array<MissionPlanningPearls> = [];
    
    static FromJson(o: any): MissionPlanningDay {
        let workDay = new MissionPlanningDay();
        workDay.dayLabel = workDay.dayLabel;
        workDay.logaDay = workDay.logaDay;
        return workDay;
    }
}

export class MissionsPlanning {
    
    workDays: Array<MissionPlanningDay> = [];
    
    static FromJson(o: any): MissionsPlanning {
        let mp = new MissionsPlanning();
        if(o.workDays.length > 0){            
            for(let workDay of o.workDays){
                let wd = MissionPlanningDay.FromJson(workDay);
                let logaPlanningEntries: Array<MissionPlanningLoga> = [];               
                let pearlsPlanningEntries: Array<MissionPlanningPearls> = [];               
                if(workDay.logaPlanningEntries.length > 0){
                    for(let logaEntry of workDay.logaPlanningEntries){
                        logaPlanningEntries.push(MissionPlanningLoga.FromJson(logaEntry));
                    }
                }
                if(workDay.pearlsPlanningEntries.length > 0){
                    for(let pearlsEntry of workDay.pearlsPlanningEntries){
                        pearlsPlanningEntries.push(MissionPlanningPearls.FromJson(pearlsEntry));
                    }
                }
                wd.logaPlanningEntries = logaPlanningEntries;
                wd.pearlsPlanningEntries = pearlsPlanningEntries;
                mp.workDays.push(wd);
            }
        
        }
        return mp;
    }
    
    getMissionsItems(operationsSettings: Map<number, OperationCustomerPlanningSetting>, storeCheckOnly: boolean = true, order: 'ASC'|'DESC' = 'ASC'): Array<MissionItem> {
        let missionsItems: Array<MissionItem> = [];
        let today = MarvDateService.GetToday();
        if(this.workDays.length > 0){
            for(let workDay of this.workDays){
                
                if(workDay.logaPlanningEntries.length > 0){
                    for(let logaEntry of workDay.logaPlanningEntries){
                        let operationSettings = operationsSettings.get(logaEntry.operation?.id);                                        
                        let missionItem = new MissionItem();

                        let missionItemRetailer = new MissionItemRetailer();
                        missionItemRetailer.id = logaEntry.retailer?.id;
                        missionItemRetailer.name = logaEntry.retailer?.name;
                        missionItemRetailer.code = logaEntry.retailer?.code;

                        let missionItemStore = new MissionItemStore();
                        missionItemStore.name = logaEntry.location?.name;
                        missionItemStore.code = logaEntry.location?.code;
                        missionItemStore.administrativeArea = logaEntry.location?.address?.administrativeArea;
                        missionItemStore.zipCode = logaEntry.location?.address?.zipCode;
                        missionItemStore.city = logaEntry.location?.address?.city;

                        let missionItemOperation = new MissionItemOperation();
                        missionItemOperation.id = logaEntry.operation?.id;
                        missionItemOperation.name = logaEntry.operation?.name;
                        missionItemOperation.typeOfMission = logaEntry.operation?.typeOfMission?.code;
                        missionItemOperation.source = 'loga';

                        let missionItemArea = new MissionItemArea();
                        missionItemArea.id = logaEntry.customerArea?.id;
                        missionItemArea.name = logaEntry.customerArea?.name;

                        let missionItemSector = new MissionItemSector();
                        missionItemSector.id = logaEntry.customerSector?.id;
                        missionItemSector.name = logaEntry.customerSector?.name;

                        missionItem.activityId = logaEntry.id;    
                        missionItem.missionId = logaEntry.mission?.id;                               
                        missionItem.retailer = missionItemRetailer;
                        missionItem.store = missionItemStore;
                        missionItem.customerArea = missionItemArea;
                        missionItem.customerSector = missionItemSector;
                        missionItem.operation = missionItemOperation;
                        missionItem.day = logaEntry.day;

                        if (logaEntry.amStartTime && logaEntry.amEndTime) {
                            missionItem.amStartTime = MarvDateService.ToTime(logaEntry.amStartTime);
                            missionItem.amEndTime = MarvDateService.ToTime(logaEntry.amEndTime);
                        }

                        if (logaEntry.pmStartTime && logaEntry.pmEndTime) {
                            missionItem.pmStartTime = MarvDateService.ToTime(logaEntry.pmStartTime);
                            missionItem.pmEndTime = MarvDateService.ToTime(logaEntry.pmEndTime);
                        }

                        missionItem.duration = (logaEntry.requestedDurationMin !== null ? MarvDateService.MinutesToStr(logaEntry.requestedDurationMin, 'h') : MarvDateService.GetMissionDuration(logaEntry.amStartTime, logaEntry.amEndTime, logaEntry.pmStartTime, logaEntry.pmEndTime));

                        if(logaEntry.worker){
                            missionItem.worker = {
                                id: logaEntry.worker.workerId,
                                name: logaEntry.worker.workerName,
                                email: logaEntry.worker.workerEmail,
                                phone: logaEntry.worker.workerMobilePhone
                            };
                        }                                        

                        /*
                        missionItem.report = {
                            id: report.id,
                            date: report.date,
                            published: logaEntry.reportPublished,
                            status: logaEntry.reportingStatus,
                            activityId: logaEntry.id
                        };
                        */                    

                        missionItem.presenceConfirmed = logaEntry.confirmedPresence;
                        missionItem.reportingCompleted = logaEntry.reportingCompleted;
                        missionItem.reportable = logaEntry.reportable;
                        missionItem.status = (!missionItem.worker ? new MissionStatus(MissionStatus.Status.PENDING_VALIDATION, logaEntry.day) : (logaEntry.day < today && (missionItem.presenceConfirmed || missionItem.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, logaEntry.day) : (logaEntry.day > today && logaEntry.mission.status.code === MissionStatus.Status.IN_PROGRESS ? new MissionStatus(MissionStatus.Status.ASSIGNED, logaEntry.day) : logaEntry.mission.status))); 

                        missionItem.profil = logaEntry.mission?.profile?.name;
                       
                        let evaluationNote: number|null = null;
                        if(logaEntry.mission?.customerFeedback != null){
                            evaluationNote = CustomerFeedbackService.CustomerFeedBackValues.find(c => c.value === logaEntry.mission.customerFeedback)?.rank || null;
                        }

                        missionItem.evaluationNote = evaluationNote;
                        missionItem.recoCompliance = logaEntry.recoCompliance;

                        if(logaEntry.plannedPurposes?.length > 0){
                            logaEntry.plannedPurposes.forEach(o => {
                                missionItem.purposes.push({
                                    name: o.name,
                                    fulfilled: true
                                });
                            });
                        }

                        missionItem.planningEntryType = 'STORECHECK';
                        missionItem.affected = (missionItem.worker?.id !== undefined);                                        
                        missionItem.showPresenceCheckInfos = (operationSettings ? operationSettings.showPresenceCheckInfos : false);
                        missionItem.source = 'loga';

                        missionsItems.push(missionItem); 
                    }
                }
                
                if(workDay.pearlsPlanningEntries.length > 0){
                    for(let pearlsEntry of workDay.pearlsPlanningEntries){
                        if ((!storeCheckOnly || pearlsEntry.planningEntryType === 'STORECHECK')) {
                            let missionItem = new MissionItem();

                            let missionItemRetailer = new MissionItemRetailer();
                            missionItemRetailer.id = pearlsEntry.retailerId;
                            missionItemRetailer.name = pearlsEntry.retailerName;
                            missionItemRetailer.code = pearlsEntry.retailerCode;

                            let missionItemStore = new MissionItemStore();
                            missionItemStore.name = pearlsEntry.storeName;
                            missionItemStore.code = pearlsEntry.storeCode;
                            missionItemStore.administrativeArea = pearlsEntry.address?.administrativeArea;
                            missionItemStore.zipCode = pearlsEntry.address?.zipCode;
                            missionItemStore.city = pearlsEntry.address?.city;

                            let missionItemOperation = new MissionItemOperation();                            
                            let op = OperationService.Operations.find(o => o.type === 'lgg' && o.id === pearlsEntry.ctxId);
                            if(op){
                                missionItemOperation.id = op.id;
                                missionItemOperation.name = op.name; 
                                missionItemOperation.typeOfMission = 'PERM';
                                missionItemOperation.source = 'lgg';
                            }
                            
                            let missionItemArea = new MissionItemArea();
                            missionItemArea.id = pearlsEntry.areaId;
                            missionItemArea.name = pearlsEntry.areaName;

                            let missionItemSector = new MissionItemSector();
                            missionItemSector.id = pearlsEntry.sectorId;
                            missionItemSector.name = pearlsEntry.sectorName;

                            let missionItemSalesRep = new MissionItemSalesRep();
                            missionItemSalesRep.id = pearlsEntry.salesRepId;
                            missionItemSalesRep.name = pearlsEntry.salesRepName;

                            missionItem.missionId = pearlsEntry.id;                                   
                            missionItem.activityId = pearlsEntry.id;
                            missionItem.retailer = missionItemRetailer;
                            missionItem.store = missionItemStore;
                            missionItem.customerArea = missionItemArea;
                            missionItem.customerSector = missionItemSector;
                            missionItem.operation = missionItemOperation;
                            missionItem.salesRep = missionItemSalesRep;
                            missionItem.day = pearlsEntry.logaDay;

                            if (pearlsEntry.startTime && pearlsEntry.endTime) {
                                missionItem.amStartTime = pearlsEntry.startTime;
                                missionItem.amEndTime = pearlsEntry.endTime;                                    
                            }

                            missionItem.duration = pearlsEntry.hrDuration;
                            missionItem.presenceConfirmed = pearlsEntry.checkInDone;
                            missionItem.reportingCompleted = (pearlsEntry.status.code === MissionStatus.Status.FULFILLED);
                            missionItem.reportable = true;
                            missionItem.status = (pearlsEntry.logaDay < today && (missionItem.presenceConfirmed || missionItem.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, pearlsEntry.logaDay) : pearlsEntry.status);
                            missionItem.profil = 'PERMANENT';

                            if(pearlsEntry.objectives?.length > 0){
                                pearlsEntry.objectives.forEach(o => {
                                    missionItem.purposes.push({
                                        name: o.code,
                                        fulfilled: o.fulfilled
                                    });
                                });
                            }

                            missionItem.description = pearlsEntry.description;
                            missionItem.planningEntryType = pearlsEntry.planningEntryType;
                            missionItem.nbPictures = pearlsEntry.nbPictures;
                            missionItem.source = 'lgg';

                            missionsItems.push(missionItem);
                        }
                    }
                }
            }
        }
        MissionItem.SortItems(missionsItems, order);
        return missionsItems;
    }
}

export class MissionPlanningFilters {
    from: number;
    to: number;
    city?: string;
    operations?: Array<number>;
    contextIds?: Array<number>;
    salesReps?: Array<number>;
    weekdays?: Array<number>;
    stores?: Array<string>;
    retailers?: Array<string>;
    customerAreaIds?: Array<number>;
    customerSectorIds?: Array<number>;
    displayOnlyStoreMissions?: boolean;
    displayCancelledMissions?: boolean;
    displayOnlyUnratedMissions?: boolean;
}